import type {
  HitAttributeHighlightResult,
  HighlightResultNormalised,
} from "./types";
import type { HitKeys } from "@/components/layout/Search/Results/models/hit";

/**
 * filter to the ones with results, optionally ignore some values
 * @param highlightResult
 * @param ignore
 */
export const filterNormalisedHighlights = (
  highlightResult: null | undefined | HighlightResultNormalised,
  ignore: string[] = [],
) => {
  if (!highlightResult) {
    return null;
  }
  // filter on matched words, optionally ignoring keys
  return Object.fromEntries(
    Object.entries(highlightResult)
      .map(([key, value]) => {
        if (ignore.includes(key)) return null;

        // ignore specific indexes within keys by sending 'key.0' to the ignore list
        const indexesToIgnore = ignore
          .filter((i) => i.startsWith(`${key}.`))
          .map((i) => Number(i.split(".")[1]));

        const filtered = value.filter(
          (item, key) =>
            item.matchedWords?.length > 0 && !indexesToIgnore.includes(key),
        );

        return filtered.length ? [key, filtered] : null;
      })
      .filter((entry) => !!entry),
  ) as HighlightResultNormalised;
};

/**
 * Get info for the next highlight, optionally ignore some in case they are already displayed and highlighted
 * @param highlights
 * @param ignore
 */
export const getNextHighlight = (
  highlights: null | HighlightResultNormalised,
  ignore: string[],
  sport?: string,
): [null | HitAttributeHighlightResult, string] => {
  const filteredHighlights = filterNormalisedHighlights(highlights, ignore);
  const highlightKey =
    filteredHighlights && Object.keys(filteredHighlights).length
      ? (Object.keys(filteredHighlights)[0] as HitKeys)
      : null;

  // drop the last `s` if there is one, add `: ` to end
  const formatTitle = (titleKey: string) => {
    // rename some titles
    let title = titleKey;

    switch (titleKey) {
      case "jockeys":
        // if its harness racing, we have drivers not jockeys
        if (sport === "HARNESS_RACING") {
          title = "drivers";
        }
        break;
      case "competitors":
        // if its racing, we have runners, not competitors
        if (
          sport === "GREYHOUNDS" ||
          sport === "HORSE_RACING" ||
          sport === "HARNESS_RACING"
        ) {
          title = "runners";
        }
        break;
      default:
      //do nothing
    }

    const firstLetter = title?.charAt(0).toUpperCase();
    // if it ends with an `s` we can drop it
    const restOfTitle = title.slice(1, title.endsWith("s") ? -1 : undefined);
    return `${firstLetter}${restOfTitle}: `;
  };

  const highlightTitle = highlightKey ? formatTitle(highlightKey) : "";

  const highlight =
    highlightKey && filteredHighlights
      ? filteredHighlights[highlightKey][0]
      : null;

  return [highlight, highlightTitle];
};
